import { runWithLoading } from '../../utils/redux/loadingOperation'
import React, { Component } from 'react'
import { Image, Keyboard, Modal, StyleSheet, Text, View } from 'react-native'
import Button from '../UIControls/Button'
import Icon from '../Icon'
import store from '../../redux/store'
import styles, { colors, hexColorToRGBa } from '../styles/global'
import { cloneDeep, isEqual } from 'lodash'
import Backend from '../../utils/BackendBridge'
import AppPopover from '../UIComponents/ModalShell/AppPopover'
import ImagePickerModal from '../UIComponents/FloatModals/ImagePickerModal'
import {
    hideFloatPopup,
    setLastAddNewContact,
    setNavigationRoute,
    setSelectedNavItem,
    setTmpInputTextContact,
    showFloatPopup,
} from '../../redux/actions'
import ContactsHelper, { PHOTO_SIZE_300, PHOTO_SIZE_50 } from './Utils/ContactsHelper'
import HelperFunctions, { execShortcutFn } from '../../utils/HelperFunctions'
import NavigationService from '../../utils/NavigationService'
import ProjectHelper from '../SettingsView/ProjectsSettings/ProjectHelper'
import Hotkeys from 'react-hot-keys'
import ChangeContactInfoModal from '../UIComponents/FloatModals/ChangeContactInfoModal'
import CustomTextInput3 from '../Feeds/CommentsTextInput/CustomTextInput3'
import {
    DV_TAB_CONTACT_PROPERTIES,
    DV_TAB_PROJECT_TEAM_MEMBERS,
    DV_TAB_USER_PROFILE,
} from '../../utils/TabNavigationConstants'
import SVGGenericUser from '../../assets/svg/SVGGenericUser'
import ContactPhoto from './ContactPhoto'
import CommentsWrapper from '../Feeds/InteractionBar/CommentsWrapper'
import { FEED_CONTACT_OBJECT_TYPE, FEED_USER_OBJECT_TYPE } from '../Feeds/Utils/FeedsConstants'
import PrivacyButton from '../UIComponents/FloatModals/PrivacyModal/PrivacyButton'
import TasksHelper from '../TaskListView/Utils/TasksHelper'
import HighlightButton from '../UIComponents/FloatModals/HighlightColorModal/HighlightButton'
import ContactMoreButton from '../UIComponents/FloatModals/MorePopupsOfEditModals/Contacts/ContactMoreButton'
import { FORM_TYPE_EDIT, FORM_TYPE_NEW } from '../NotesView/NotesDV/EditorView/EditorsGroup/EditorsConstants'
import { translate } from '../../i18n/TranslationService'
import {
    addContactToProject,
    copyContactToProject,
    setProjectContactHighlight,
    setProjectContactName,
    setProjectContactPicture,
} from '../../utils/backends/Contacts/contactsFirestore'
import { MENTION_MODAL_CONTACTS_TAB } from '../Feeds/CommentsTextInput/textInputHelper'
import { createSingleFlightSubmit } from '../../hooks/useSingleFlightSubmit'
import Spinner from '../UIComponents/Spinner'
import { setUserHighlightInProject, setUserPrivacyInProject } from '../../utils/backends/Users/usersFirestore'
import { CONTACT_STATUS_FILTER_UNASSIGNED } from '../ContactStatusFilters/contactStatusFilterConstants'

export default class EditContact extends Component {
    // AT-2508 - the form can be dismissed while a creation is still in flight (react-dismissible
    // still owns Escape and outside clicks, deliberately, so a hung write can never trap the
    // user behind a disabled Cancel), so every continuation below has to know whether there is
    // still a component to talk to.
    _isMounted = false

    constructor(props) {
        super(props)
        const storeState = store.getState()
        // When creating a new contact, use the current contact status filter as the default status
        const defaultStatusId =
            storeState.contactStatusFilter === CONTACT_STATUS_FILTER_UNASSIGNED ? null : storeState.contactStatusFilter
        const contact = this.props.isNew ? ContactsHelper.getDefaultContactInfo(defaultStatusId) : this.props.contact
        const clonedContact = cloneDeep(contact)

        this.state = {
            loggedUserId: store.getState().loggedUser.uid,
            loggedUserProjects: storeState.loggedUserProjects,
            loggedUserProjectsMap: storeState.loggedUserProjectsMap,
            contact: contact,
            tmpContact: clonedContact,
            isEmail: false,
            contactChanged: false,
            // AT-2508 - adding a person takes a visible moment. The form stays on screen and
            // says so until the write is acknowledged, instead of vanishing into a list that
            // will not show the new row for several seconds yet.
            creatingContact: false,
            creationError: null,
            showInfoModal: false,
            showPictureModal: false,
            showCopyContactModal: false,
            contactToCopy: null,
            smallScreen: storeState.smallScreen,
            isMiddleScreen: storeState.isMiddleScreen,
            unsubscribe: store.subscribe(this.updateState),
        }
        this.inputTextRef = React.createRef()
    }

    componentDidMount() {
        this._isMounted = true
        store.dispatch(setLastAddNewContact({ projectId: this.props.projectId }))
        document.addEventListener('keydown', this.onPressKey)
    }

    componentWillUnmount() {
        this._isMounted = false
        document.removeEventListener('keydown', this.onPressKey)
        this.state.unsubscribe()
    }

    updateState = () => {
        if (!this._isMounted) return
        const storeState = store.getState()

        this.setState({
            smallScreen: storeState.smallScreen,
            isMiddleScreen: storeState.isMiddleScreen,
            loggedUserProjectsMap: storeState.loggedUserProjectsMap,
        })

        if (storeState.showGlobalSearchPopup) {
            this.dismiss()
        }
    }

    enterActionKey = e => {
        const { showFloatPopup } = store.getState()
        // While the contact is being written the form is locked and showing its progress; a
        // stray Enter must not close it or queue a second write (AT-2508).
        if (this.state.creatingContact) return
        if (!showFloatPopup) {
            const { isNew } = this.props
            const { tmpContact } = this.state
            if (e) {
                e.preventDefault()
                e.stopPropagation()
            }
            if (isNew) {
                if (tmpContact.displayName.length > 0) {
                    this.addProjectContact()
                } else {
                    this.dismiss()
                }
            } else {
                this.onSubmit()
            }
        }
    }

    onPressKey = e => {
        if (e.key === 'Enter') {
            this.enterActionKey(e)
        }
    }

    updateContactField = async (field, value) => {
        const { tmpContact, contact } = this.state
        if (typeof value === 'string') {
            tmpContact[field] = value.trim().length <= 0 ? '' : value

            if (field === 'description') {
                tmpContact.extendedDescription = value
                tmpContact.description = TasksHelper.getTaskNameWithoutMeta(value)
            }
        } else if (field === 'isPrivate') {
            tmpContact.isPrivate = value.isPrivate
            tmpContact.isPublicFor = value.isPublicFor
        } else {
            tmpContact[field] = value
        }

        if (!isEqual(tmpContact, contact)) {
            this.setState({ tmpContact: tmpContact, contactChanged: true })
        } else {
            this.setState({ contactChanged: false })
        }
    }

    changeInfo = info => {
        this.updateContactField('role', info.role)
        this.updateContactField('company', info.company)
        this.updateContactField('description', info.description)
    }

    changePropertyValue = value => {
        const { projectIndex, contact, dismissibleRef, isMember, projectId } = this.props

        dismissibleRef?.toggleModal()

        if (isMember) {
            ProjectHelper.setUserInfoInProject(
                projectId,
                projectIndex,
                contact.uid,
                value.company.trim(),
                value.role.trim(),
                value.description.trim()
            )
        } else {
            ProjectHelper.setContactInfoInProject(
                projectIndex,
                contact,
                contact.uid,
                value.company.trim(),
                contact.company,
                value.role.trim(),
                contact.role,
                value.description.trim(),
                contact.extendedDescription
            )
        }
    }

    onChangeInputText = text => {
        const { isNew } = this.props
        this.updateContactField('displayName', text)
        this.setState({ isEmail: text !== '' && text.indexOf('@') >= 0 })
        if (isNew) store.dispatch(setTmpInputTextContact(text))
    }

    onMentionSelected = (item, activeTab) => {
        const { projectId } = this.props
        // Only check for contacts mentions
        if (activeTab !== MENTION_MODAL_CONTACTS_TAB) return
        // Skip if no projectId or if the contact is from the same project
        if (!projectId || item.projectId === projectId) return
        // Skip if it's an assistant
        if (item.isAssistant) return
        // Skip if the contact is a team member (has no recorderUserId)
        if (!item.recorderUserId) return

        // Contact is from a different project - ask if user wants to copy
        this.setState({ contactToCopy: item, showCopyContactModal: true })
    }

    handleCopyContact = async () => {
        const { projectId } = this.props
        const { contactToCopy } = this.state
        if (contactToCopy) {
            await copyContactToProject(projectId, contactToCopy)
        }
        this.setState({ showCopyContactModal: false, contactToCopy: null })
    }

    handleCancelCopy = () => {
        this.setState({ showCopyContactModal: false, contactToCopy: null })
    }

    changePicture = async value => {
        const { projectId, contact, dismissibleRef, isNew } = this.props
        if (!isNew) {
            await setProjectContactPicture(projectId, contact, contact.uid, value)
            dismissibleRef?.toggleModal()
        } else {
            this.updateContactField('photoURL', value)
        }
    }

    setPrivacyBeforeSave = (isPrivate, isPublicFor) => {
        const { projectIndex, contact, dismissibleRef, isMember } = this.props
        const { loggedUserProjects } = store.getState()
        const project = loggedUserProjects[projectIndex]

        if (isMember) {
            setUserPrivacyInProject(project, contact, isPrivate, isPublicFor)
            dismissibleRef?.toggleModal()
        } else {
            const privacy = { isPrivate, isPublicFor }
            this.updateContactField('isPrivate', privacy)
        }
    }

    setHighlightBeforeSave = (highlightColor, directAction = false) => {
        const { projectIndex, contact, dismissibleRef, isMember } = this.props
        const { loggedUserProjects, loggedUser } = store.getState()
        const project = loggedUserProjects[projectIndex]

        if (directAction) {
            if (isMember) {
                setUserHighlightInProject(project, contact, highlightColor)
            } else {
                setProjectContactHighlight(project.id, contact, contact.uid, highlightColor)
            }
            dismissibleRef?.toggleModal()
        } else {
            this.updateContactField('hasStar', highlightColor)
        }
    }

    setPhoneBeforeSave = phone => {
        this.updateContactField('phone', phone)
    }

    setEmailBeforeSave = email => {
        const emails = Array.from(
            new Set(
                String(email || '')
                    .split('\n')
                    .map(item => item.trim().toLowerCase())
                    .filter(Boolean)
            )
        )
        this.updateContactField('email', emails[0] || '')
        this.updateContactField('emails', emails)
    }

    showInfoModal = () => {
        this.setState({ showInfoModal: true })
        store.dispatch(showFloatPopup())
    }

    hideInfoModal = () => {
        // This timeout is necessary to stop the propagation of the click
        // to close the Modal, and reach the dismiss event of the EditContact
        const { showFloatPopup } = store.getState()
        if (showFloatPopup === 1) {
            setTimeout(async () => {
                this.setState({ showInfoModal: false })
                store.dispatch(hideFloatPopup())
                this.focusInput()
            })
        }
    }

    showPictureModal = () => {
        this.setState({ showPictureModal: true })
        store.dispatch(showFloatPopup())
    }

    hidePictureModal = () => {
        // This timeout is necessary to stop the propagation of the click
        // to close the Modal, and reach the dismiss event of the EditContact
        setTimeout(async () => {
            this.setState({ showPictureModal: false })
            store.dispatch(hideFloatPopup())
            this.focusInput()
        }, 200)
    }

    focusInput = () => {
        if (this.inputTextRef && this.inputTextRef.current) {
            this.inputTextRef.current.focus()
        }
    }

    blurInput = () => {
        if (this.inputTextRef && this.inputTextRef.current) {
            this.inputTextRef.current.blur()
        }
    }
    onUserPress = () => {
        const { contact, isMember, projectIndex } = this.props
        const { loggedUserProjects } = this.state
        NavigationService.navigate(isMember ? 'UserDetailedView' : 'ContactDetailedView', {
            contact: contact,
            project: loggedUserProjects[projectIndex],
        })
        store.dispatch(setSelectedNavItem(isMember ? DV_TAB_USER_PROFILE : DV_TAB_CONTACT_PROPERTIES))
    }

    onSubmit = () => {
        const { tmpContact, contactChanged, loggedUserId } = this.state
        const { dismissibleRef, contact, projectId, isMember } = this.props

        const userIsLoggedUser = loggedUserId === contact.uid
        const loggedUserIsCreator = loggedUserId === contact.recorderUserId
        const loggedUserCanUpdateObject = isMember
            ? userIsLoggedUser || !ProjectHelper.checkIfLoggedUserIsNormalUserInGuide(projectId)
            : loggedUserIsCreator || !ProjectHelper.checkIfLoggedUserIsNormalUserInGuide(projectId)

        if (contactChanged && loggedUserCanUpdateObject) {
            setProjectContactName(
                projectId,
                tmpContact,
                tmpContact.uid,
                tmpContact.displayName.trim(),
                contact.displayName
            )
        }
        dismissibleRef?.toggleModal()
    }

    render() {
        const {
            smallScreen,
            isMiddleScreen: mobile,
            isEmail,
            tmpContact,
            contactChanged,
            showInfoModal,
            showPictureModal,
            showCopyContactModal,
            contactToCopy,
            loggedUserProjectsMap,
            loggedUserId,
            creatingContact,
            creationError,
        } = this.state
        const { onCancelAction, style, projectId, isNew, projectIndex, isMember, dismissibleRef, contact } = this.props
        const buttonItemStyle = { marginHorizontal: smallScreen ? 4 : 2 }
        const userRole = ProjectHelper.getUserRoleInProject(projectId, tmpContact.uid, tmpContact.role)
        const userCompany = ProjectHelper.getUserCompanyInProject(projectId, tmpContact.uid, tmpContact.company)
        const userDescription = ProjectHelper.getUserDescriptionInProject(
            projectId,
            tmpContact.uid,
            tmpContact.description,
            tmpContact.extendedDescription,
            true
        )
        const highlightColor = ProjectHelper.getUserHighlightInProject(projectIndex, tmpContact)
        const contactPhoto50 = ContactsHelper.getContactPhotoURL(tmpContact, isMember, PHOTO_SIZE_50)
        const contactPhoto300 = ContactsHelper.getContactPhotoURL(tmpContact, isMember, PHOTO_SIZE_300)
        tmpContact.hasStar = highlightColor

        const userIsLoggedUser = isNew || loggedUserId === contact.uid
        const loggedUserIsCreator = isNew || loggedUserId === contact.recorderUserId
        const loggedUserCanUpdateObject = isMember
            ? userIsLoggedUser || !ProjectHelper.checkIfLoggedUserIsNormalUserInGuide(projectId)
            : loggedUserIsCreator || !ProjectHelper.checkIfLoggedUserIsNormalUserInGuide(projectId)

        // While the contact is being written, every control that would start a second write or
        // throw away the in-flight one is locked (AT-2508). The two `Hotkeys` wrappers have
        // always been stricter than the buttons they wrap - they drop the `isNew` qualifier, so
        // alt+O / alt+1 are inert on an unchanged EDIT form while the buttons stay live. That
        // difference predates this change and is preserved rather than quietly flattened.
        const secondaryButtonsDisabled = (isNew && !contactChanged) || isEmail || creatingContact
        const hotkeysDisabled = !contactChanged || isEmail || creatingContact

        return (
            <View
                style={[localStyles.container, mobile ? localStyles.containerUnderBreakpoint : undefined, style]}
                data-edit-contact={`${isNew ? 'new-contact' : contact.uid}`}
            >
                <View style={[localStyles.inputContainer]}>
                    <View
                        style={[
                            localStyles.icon,
                            !isNew && { top: 7, left: 15 },
                            mobile && (!isNew ? { left: 8 } : localStyles.iconMobile),
                        ]}
                    >
                        {isNew && creatingContact ? (
                            // The leading "+" turns into a spinner in place, so the row the user
                            // is looking at is itself the progress indicator (AT-2508).
                            <View testID="edit-contact-creating-spinner">
                                <Spinner containerSize={24} spinnerSize={20} containerColor={'transparent'} />
                            </View>
                        ) : isNew ? (
                            <Icon name={'plus-square'} size={24} color={colors.Primary100} />
                        ) : (
                            <View style={localStyles.userPhoto}>
                                {tmpContact.photoURL != null && tmpContact.photoURL !== '' ? (
                                    <View>
                                        <ContactPhoto
                                            uri={contactPhoto50}
                                            style={localStyles.image}
                                            spinnerContainerStyle={{ backgroundColor: colors.Gray200 }}
                                        />
                                    </View>
                                ) : (
                                    <SVGGenericUser
                                        width={48}
                                        height={48}
                                        svgid={`ci_p_${tmpContact.uid}_${projectIndex}`}
                                    />
                                )}
                            </View>
                        )}
                    </View>

                    {isMember ? (
                        <Text
                            style={[
                                localStyles.input,
                                mobile ? localStyles.inputUnderBreakpoint : undefined,
                                { height: 62, paddingTop: 19 },
                            ]}
                        >
                            {tmpContact.displayName}
                        </Text>
                    ) : (
                        <CustomTextInput3
                            fixedHeight={62}
                            ref={this.inputTextRef}
                            initialTextExtended={isNew ? store.getState().tmpInputTextContact : tmpContact.displayName}
                            returnKeyType={'done'}
                            placeholder={translate('Type email or name to add person')}
                            containerStyle={[localStyles.input, mobile ? localStyles.inputUnderBreakpoint : undefined]}
                            autoFocus={true}
                            multiline={false}
                            numberOfLines={1}
                            onChangeText={this.onChangeInputText}
                            placeholderTextColor={colors.Text03}
                            projectId={projectId}
                            forceTriggerEnterActionForBreakLines={this.enterActionKey}
                            disabledEdition={!loggedUserCanUpdateObject || creatingContact}
                            onMentionSelected={isNew ? undefined : this.onMentionSelected}
                        />
                    )}
                </View>

                {!!creationError && (
                    // Sits between the input and the button bar, so a failed creation explains
                    // itself right where the name the user typed is still visible (AT-2508).
                    <View
                        style={localStyles.errorContainer}
                        testID="edit-contact-creation-error"
                        accessibilityRole="alert"
                    >
                        <Icon name="alert-circle" size={16} color={colors.UtilityRed200} />
                        <Text style={localStyles.errorText}>{creationError}</Text>
                    </View>
                )}

                <View style={localStyles.buttonContainer}>
                    <View style={[localStyles.buttonSection]}>
                        <View style={smallScreen ? undefined : { marginRight: 32 }}>
                            <Hotkeys
                                keyName={'alt+O'}
                                disabled={hotkeysDisabled}
                                onKeyDown={(sht, event) =>
                                    execShortcutFn(
                                        this.openBtnRef,
                                        isNew ? this.openBeforeCreate : this.onUserPress,
                                        event
                                    )
                                }
                                filter={e => true}
                            >
                                <Button
                                    ref={ref => (this.openBtnRef = ref)}
                                    title={smallScreen ? null : translate('Open nav')}
                                    type={'secondary'}
                                    noBorder={smallScreen}
                                    icon={'maximize-2'}
                                    iconColor={'#8A94A6'}
                                    buttonStyle={buttonItemStyle}
                                    onPress={isNew ? this.openBeforeCreate : this.onUserPress}
                                    disabled={secondaryButtonsDisabled}
                                    shortcutText={'O'}
                                />
                            </Hotkeys>
                        </View>

                        {loggedUserCanUpdateObject && (
                            <AppPopover
                                content={
                                    <ChangeContactInfoModal
                                        projectId={projectId}
                                        closePopover={this.hideInfoModal}
                                        onSaveData={value =>
                                            !isNew ? this.changePropertyValue(value) : this.changeInfo(value)
                                        }
                                        currentRole={userRole ? userRole : ''}
                                        currentCompany={userCompany ? userCompany : ''}
                                        currentDescription={userDescription ? userDescription : ''}
                                    />
                                }
                                onClickOutside={this.hideInfoModal}
                                isOpen={showInfoModal}
                                position={['bottom', 'left', 'right', 'top']}
                                padding={4}
                                align={'end'}
                                contentLocation={mobile ? null : undefined}
                            >
                                <Hotkeys
                                    keyName={'alt+I'}
                                    onKeyDown={(sht, event) =>
                                        execShortcutFn(this.infoBtnRef, this.showInfoModal, event)
                                    }
                                    filter={e => true}
                                >
                                    <Button
                                        ref={ref => (this.infoBtnRef = ref)}
                                        title={smallScreen ? null : translate('Info')}
                                        type={'ghost'}
                                        noBorder={smallScreen}
                                        icon={'info'}
                                        buttonStyle={buttonItemStyle}
                                        onPress={this.showInfoModal}
                                        disabled={secondaryButtonsDisabled}
                                        shortcutText={'I'}
                                    />
                                </Hotkeys>
                            </AppPopover>
                        )}

                        {!isMember && loggedUserCanUpdateObject && (
                            <AppPopover
                                content={
                                    <ImagePickerModal
                                        closePopover={this.hidePictureModal}
                                        onSavePicture={this.changePicture}
                                        picture={contactPhoto300 !== '' ? contactPhoto300 : undefined}
                                        onOpenModal={this.blurInput}
                                    />
                                }
                                onClickOutside={this.hidePictureModal}
                                isOpen={showPictureModal}
                                position={['bottom', 'left', 'right', 'top']}
                                padding={4}
                                align={'end'}
                                contentLocation={mobile ? null : undefined}
                            >
                                <Hotkeys
                                    keyName={'alt+1'}
                                    disabled={hotkeysDisabled}
                                    onKeyDown={(sht, event) =>
                                        execShortcutFn(this.pictureBtnRef, this.showPictureModal, event)
                                    }
                                    filter={e => true}
                                >
                                    <Button
                                        ref={ref => (this.pictureBtnRef = ref)}
                                        title={smallScreen ? null : translate('Picture')}
                                        type={'ghost'}
                                        noBorder={smallScreen}
                                        icon={contactPhoto50 === '' ? 'image' : <Picture photoURL={contactPhoto50} />}
                                        buttonStyle={buttonItemStyle}
                                        onPress={this.showPictureModal}
                                        disabled={secondaryButtonsDisabled}
                                        shortcutText={'1'}
                                    />
                                </Hotkeys>
                            </AppPopover>
                        )}

                        {(isMember || isNew) && loggedUserCanUpdateObject && (
                            <PrivacyButton
                                projectId={projectId}
                                object={tmpContact}
                                objectType={isMember ? FEED_USER_OBJECT_TYPE : FEED_CONTACT_OBJECT_TYPE}
                                disabled={secondaryButtonsDisabled}
                                savePrivacyBeforeSaveObject={isNew && this.setPrivacyBeforeSave}
                                inEditComponent={true}
                                style={buttonItemStyle}
                                shortcutText={'P'}
                            />
                        )}

                        {!isNew && (
                            <CommentsWrapper
                                style={buttonItemStyle}
                                projectId={projectId}
                                commentedFeed={isMember ? { userId: tmpContact.uid } : { contactId: tmpContact.uid }}
                                smallScreen={smallScreen}
                                extraFunction={dismissibleRef?.toggleModal}
                                userGettingKarmaId={contact.recorderUserId ? contact.recorderUserId : contact.uid}
                                assistantId={tmpContact.assistantId}
                            />
                        )}

                        {loggedUserCanUpdateObject && (
                            <HighlightButton
                                projectId={projectId}
                                object={tmpContact}
                                objectType={isMember ? FEED_USER_OBJECT_TYPE : FEED_CONTACT_OBJECT_TYPE}
                                disabled={secondaryButtonsDisabled}
                                saveHighlightBeforeSaveObject={color => this.setHighlightBeforeSave(color, !isNew)}
                                inEditComponent={true}
                                style={buttonItemStyle}
                                shortcutText={'H'}
                            />
                        )}

                        {loggedUserCanUpdateObject && (
                            <ContactMoreButton
                                formType={isNew ? FORM_TYPE_NEW : FORM_TYPE_EDIT}
                                projectId={projectId}
                                contact={tmpContact}
                                isMember={isMember}
                                buttonStyle={buttonItemStyle}
                                disabled={secondaryButtonsDisabled}
                                savePhoneBeforeSaveContact={isNew && this.setPhoneBeforeSave}
                                saveEmailBeforeSaveContact={isNew && this.setEmailBeforeSave}
                                dismissEditMode={this.dismiss}
                            />
                        )}
                    </View>

                    <View style={[localStyles.buttonSection, localStyles.buttonSectionRight]}>
                        {smallScreen ? undefined : (
                            <Button
                                title={translate('Cancel')}
                                type={'secondary'}
                                buttonStyle={{ marginHorizontal: 4 }}
                                onPress={onCancelAction}
                                disabled={creatingContact}
                                shortcutText={'Esc'}
                            />
                        )}

                        <Button
                            title={
                                smallScreen
                                    ? null
                                    : contactChanged
                                      ? translate(isEmail ? 'Invite' : isNew ? 'Add Person' : 'Save')
                                      : 'Ok'
                            }
                            type={'primary'}
                            icon={
                                smallScreen && !creatingContact
                                    ? contactChanged
                                        ? isNew
                                            ? 'plus'
                                            : 'save'
                                        : 'x'
                                    : null
                            }
                            onPress={() => (isNew ? this.addProjectContact() : this.onSubmit())}
                            shortcutText={creatingContact ? '' : 'Enter'}
                            disabled={creatingContact}
                            processing={creatingContact}
                            processingTitle={smallScreen ? '' : `${translate('Adding person')}...`}
                        />
                    </View>
                </View>
                {showCopyContactModal && contactToCopy && (
                    <Modal
                        visible={true}
                        transparent={true}
                        animationType="fade"
                        onRequestClose={this.handleCancelCopy}
                    >
                        <View style={localStyles.copyModalOverlay}>
                            <View style={localStyles.copyModalContainer}>
                                <Text style={localStyles.copyModalTitle}>
                                    {translate('Copy contact to this project?')}
                                </Text>
                                <Text style={localStyles.copyModalText}>
                                    {contactToCopy.displayName} {translate('is from')}{' '}
                                    {loggedUserProjectsMap[contactToCopy.projectId]?.name ||
                                        translate('another project')}
                                    .
                                </Text>
                                <Text style={localStyles.copyModalSubtext}>
                                    {translate('Only basic contact info will be copied')}
                                </Text>
                                <View style={localStyles.copyModalButtons}>
                                    <Button
                                        type={'secondary'}
                                        title={translate('No')}
                                        onPress={this.handleCancelCopy}
                                        buttonStyle={{ marginRight: 8 }}
                                    />
                                    <Button
                                        type={'primary'}
                                        title={translate('Yes, copy')}
                                        onPress={this.handleCopyContact}
                                    />
                                </View>
                            </View>
                        </View>
                    </Modal>
                )}
            </View>
        )
    }

    // Enter reaches this editor through the document listener, Quill's newline
    // callback and the done button at once, and `addContactToProject` mints a
    // new contact id per call, so creation is guarded.
    addProjectContact = (openDetails = false) => {
        const { tmpContact, contactChanged, creatingContact } = this.state
        // A creation is already on screen and in flight - neither start another nor dismiss the
        // progress the user is looking at (AT-2508).
        if (creatingContact) return
        // Nothing to create: dismiss without consuming the single submission.
        if (!contactChanged || tmpContact.displayName.trim().length === 0) {
            this.dismiss()
            return
        }
        return this.runAddProjectContact(openDetails)
    }

    runAddProjectContact = createSingleFlightSubmit(async (openDetails = false) => {
        const { projectId } = this.props
        const { tmpContact, contactChanged } = this.state
        tmpContact.displayName = tmpContact.displayName.trim()

        if (!contactChanged || tmpContact.displayName.length === 0) {
            this.dismiss()
            return
        }

        // If contact name is an email, then send an invitation. Nothing local is written, and
        // the view navigates away immediately, so this branch keeps its old shape.
        if (HelperFunctions.isValidEmail(tmpContact.displayName)) {
            store.dispatch(setTmpInputTextContact(''))
            Backend.inviteUserToProject(tmpContact.displayName, projectId, tmpContact.recorderUserId)
            store.dispatch([setSelectedNavItem(DV_TAB_PROJECT_TEAM_MEMBERS)])
            NavigationService.navigate('ProjectDetailedView', {
                projectIndex: ProjectHelper.getProjectIndexById(projectId),
            })
            this.dismiss()
            return
        }

        // AT-2508: the form used to be torn down here, synchronously, while the write was still
        // in flight - so the user was left looking at an unchanged list with no spinner, no row
        // and no sign anything had happened. It now stays up in an "Adding person..." state
        // until the write is acknowledged, and the optimistic row published by
        // `addContactToProject` carries the rest of the wait (the several seconds until the
        // access projection lands and the list query can finally see the contact).
        return runWithLoading('create_contact', async () => {
            this.setState({ creatingContact: true, creationError: null })

            let createdContact = null

            try {
                if (tmpContact.photoURL !== '' && tmpContact.photoURL != null) {
                    const src =
                        typeof tmpContact.photoURL === 'string'
                            ? tmpContact.photoURL
                            : URL.createObjectURL(tmpContact.photoURL)

                    const resized50 = (await HelperFunctions.resizeImage(src, PHOTO_SIZE_50)).uri
                    const resized300 = (await HelperFunctions.resizeImage(src, PHOTO_SIZE_300)).uri

                    tmpContact.photoURL = await HelperFunctions.convertURItoBlob(tmpContact.photoURL)
                    tmpContact.photoURL50 = await HelperFunctions.convertURItoBlob(resized50)
                    tmpContact.photoURL300 = await HelperFunctions.convertURItoBlob(resized300)
                }

                await addContactToProject(projectId, tmpContact, contact => {
                    createdContact = contact
                })
            } catch (error) {
                this.onFailedAddProjectContact(error)
                return
            }

            // The contact exists now, so the draft that survives a dismissed form is spent. Closing
            // the editor here - rather than at submit time - is what keeps the progress state on
            // screen for the whole wait. It is skipped when the form is already gone: `dismiss` runs
            // `onCancelAction`, which is a toggle, so calling it on a dismissed form would REOPEN it.
            store.dispatch(setTmpInputTextContact(''))
            if (this._isMounted) this.dismiss()

            if (openDetails && createdContact) {
                NavigationService.navigate('ContactDetailedView', {
                    contact: createdContact,
                    project: { id: projectId, index: ProjectHelper.getProjectIndexById(projectId) },
                })
                store.dispatch(setSelectedNavItem(DV_TAB_CONTACT_PROPERTIES))
            }
        })
    })

    /**
     * AT-2508 - a failed creation used to silently close the form and drop the typed name, which
     * is the exact "did something go wrong?" moment this task is about. Keep the form, keep the
     * text, say what happened, allow a retry.
     */
    onFailedAddProjectContact = error => {
        console.error('[contacts] Could not add the person', error)

        // `runAddProjectContact` is one-shot by design, and it observed this rejection as a
        // resolution (it is handled here), so it would stay locked and silently swallow the
        // retry. Release it explicitly.
        this.runAddProjectContact.reset()

        if (!this._isMounted) return

        this.setState({
            creatingContact: false,
            creationError: translate('Person could not be added'),
        })
    }

    openBeforeCreate = () => {
        this.addProjectContact(true)
    }

    dismiss = () => {
        const { onCancelAction } = this.props
        onCancelAction()
        Keyboard.dismiss()
    }
}

export class Picture extends React.Component {
    shouldComponentUpdate(nextProps, nextState, nextContext) {
        return !isEqual(this.props, nextProps)
    }

    render() {
        const { photoURL } = this.props
        const src = typeof photoURL === 'string' ? photoURL : photoURL?.length > 0 ? URL.createObjectURL(photoURL) : ''

        return (
            <View style={localStyles.picture}>
                <Image style={{ width: 24, height: 24 }} source={src} />
            </View>
        )
    }
}

const localStyles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#ffffff',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: colors.Grey200,
        borderRadius: 4,
        boxShadow: '0px 4px 8px rgba(0,0,0,0.08)',
        elevation: 3,
        marginLeft: -16,
        marginRight: -16,
        marginBottom: 16,
    },
    containerUnderBreakpoint: {
        marginLeft: -8,
        marginRight: -8,
    },
    buttonContainer: {
        flex: 1,
        flexDirection: 'row',
        backgroundColor: colors.Grey100,
        borderTopWidth: 1,
        borderStyle: 'solid',
        borderTopColor: colors.Gray300,
        paddingVertical: 8,
        paddingHorizontal: 9,
    },
    buttonSection: {
        flexDirection: 'row',
        flexGrow: 1,
    },
    buttonSectionRight: {
        justifyContent: 'flex-end',
    },
    // AT-2508 - sits between the input and the button bar, so a failed creation explains itself
    // right where the name the user typed is still visible.
    errorContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingBottom: 8,
    },
    errorText: {
        ...styles.body2,
        color: colors.UtilityRed200,
        marginLeft: 8,
        flex: 1,
    },
    inputContainer: {
        //height: 56,
        overflow: 'hidden',
        //flexDirection: 'row',
    },
    icon: {
        position: 'absolute',
        padding: 0,
        margin: 0,
        left: 27,
        top: 15,
    },
    iconMobile: {
        left: 19,
    },
    picture: {
        width: 24,
        height: 24,
        borderRadius: 100,
        overflow: 'hidden',
    },
    input: {
        ...styles.body1,
        paddingTop: 14,
        paddingBottom: 12,
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        marginLeft: 52,
        paddingLeft: 20,
        paddingRight: 16,
        textAlignVertical: 'top',
    },
    inputUnderBreakpoint: {
        paddingLeft: 12,
        paddingRight: 8,
    },
    userPhoto: {
        width: 48,
        height: 48,
        borderRadius: 100,
        overflow: 'hidden',
    },
    image: {
        width: 48,
        height: 48,
        borderRadius: 100,
    },
    copyModalOverlay: {
        flex: 1,
        backgroundColor: hexColorToRGBa(colors.Text03, 0.24),
        justifyContent: 'center',
        alignItems: 'center',
    },
    copyModalContainer: {
        backgroundColor: colors.Secondary400,
        borderRadius: 4,
        padding: 16,
        margin: 16,
        maxWidth: 320,
        boxShadow: '0px 4px 16px rgba(78,93,120,0.56)',
        elevation: 5,
    },
    copyModalTitle: {
        ...styles.title7,
        color: '#ffffff',
        marginBottom: 8,
    },
    copyModalText: {
        ...styles.body2,
        color: colors.Text02,
        marginBottom: 4,
    },
    copyModalSubtext: {
        ...styles.caption1,
        color: colors.Text03,
        marginBottom: 16,
    },
    copyModalButtons: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },
})
